import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { callAPI } from "../helper/apiUtils";
import { BASE_URL } from "../helper/constants";
import { $user } from "../helper/UserFactory";
import { $crud } from "../helper/CrudFactory";
import LanguageContext from "../helper/LanguageContext";
import { DE } from "../Languages/DE";
import { EN } from "../Languages/EN";
import { KJUUP_COLORS } from "../helper/constants";
import { FiSearch } from "react-icons/fi";
import { Oval } from "react-loader-spinner";
import "./RoomSelection.css";
import CompanyListDialog from "../pages/Project/Dialogs/CompanyListDialog";
import { attachTeamsCopy } from "./chat/TeamsContacts";

// teamsPeople / teamsAccountId: a Kjuup + Teams chat (MemberSelection). Right after the group is
// created it gets its Teams copy (POST /teams/bridges); a failed copy keeps and opens the group.
function RoomSelection({ selectedMemberIds, members, chatGroup, company, onBack, onComplete, teamsPeople = [], teamsAccountId = null }) {
  const { isGerm, strings } = useContext(LanguageContext);
  const currentUser = $user.get();
  
  // Check if this is an internal flow (must be boolean, not UUID)
  const isInternal = !!(company && company.id);
  const primaryColor = isInternal ? "var(--kjuup-internal)" : (KJUUP_COLORS?.base_color || "var(--kjuup-accent)");

  // State
  const [roomName, setRoomName] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [existingRooms, setExistingRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(50);
  const searchTimeoutRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const isInitialMount = useRef(true);
  const [inputFocused, setInputFocused] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [pendingPrivacyChoice, setPendingPrivacyChoice] = useState(false);
  const [roomParams, setRoomParams] = useState({
    companyId: "",
    companyName: "",
  });
  // Loading stage state: null | "creating" | "opening"
  const [loadingStage, setLoadingStage] = useState(null);

  // Get companies on mount (only if no company provided from member selection)
  const getCompanies = async () => {
    // If company is provided from member selection (internal chat), use it
    if (company && company.id) {
      setRoomParams({
        companyId: company.id,
        companyName: company.name,
      });
      return;
    }
    
    try {
      const { data } = await callAPI(`/company/my`, {}, "GET");
      if (data.status) {
        if (data.companies.length === 1) {
          setRoomParams({
            companyId: data.companies[0].id,
            companyName: data.companies[0].name,
          });
        } else {
          setCompanies(data.companies);
        }
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  useEffect(() => {
    getCompanies();
  }, [company]);

  // Auto-focus input on mount with delay for modal animation
  useEffect(() => {
    const focusTimeout = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 200);
    return () => clearTimeout(focusTimeout);
  }, []);

  // Initial load on mount
  useEffect(() => {
    const initialLoad = async () => {
      try {
        setLoadingRooms(true);
        
        // If no members selected, pass only the current user's ID
        const usersToPass = (selectedMemberIds && selectedMemberIds.length > 0) 
          ? selectedMemberIds 
          : [currentUser?.id];
        
        const requestBody = {
          users: usersToPass,
          page: 1,
          limit: limit,
          search: "",
        };
        if (company && company.id) {
          requestBody.company = company.id;
        }
        const { data } = await callAPI(
          `/building/getSuggestedRooms`,
          {},
          "POST",
          requestBody
        );

        console.log("RoomSelection - Initial API Response:", data);

        if (data && data.status && data.rooms && data.rooms.data) {
          // Sort by matchedMembers (highest first), then by name
          const sortedRooms = [...data.rooms.data].sort((a, b) => {
            if (b.matchedMembers !== a.matchedMembers) {
              return b.matchedMembers - a.matchedMembers;
            }
            return (a.name || "").localeCompare(b.name || "");
          });

          setExistingRooms(sortedRooms);
          // Check if there are more pages based on totalCount
          const totalPages = Math.ceil((data.rooms.totalCount || 0) / limit);
          setHasMore(totalPages > 1);
        } else {
          setExistingRooms([]);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error in initial load:", error);
        $crud.notify({
          type: "error",
          message: strings.error_loading_groups,
        });
        setExistingRooms([]);
        setHasMore(false);
      } finally {
        setLoadingRooms(false);
      }
    };

    // Always call initialLoad (even when no members selected)
    initialLoad();
  }, [selectedMemberIds, company]);

  // Handle search - debounce typing, reload when cleared
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Initial empty load is handled by the mount effect above — skip duplicate call
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // If search is empty, reload initial data
    if (!searchValue.trim()) {
      setDebouncedSearch("");
      setPage(1);
      setExistingRooms([]);
      setHasMore(true);
      // Reload initial data
      const reloadInitial = async () => {
        try {
          setLoadingRooms(true);
          
          // If no members selected, pass only the current user's ID
          const usersToPass = (selectedMemberIds && selectedMemberIds.length > 0) 
            ? selectedMemberIds 
            : [currentUser?.id];
          
          const requestBody = {
            users: usersToPass,
            page: 1,
            limit: limit,
            search: "",
          };
          if (company && company.id) {
            requestBody.company = company.id;
          }
          const { data } = await callAPI(
            `/building/getSuggestedRooms`,
            {},
            "POST",
            requestBody
          );

          if (data && data.status && data.rooms && data.rooms.data) {
            const sortedRooms = [...data.rooms.data].sort((a, b) => {
              if (b.matchedMembers !== a.matchedMembers) {
                return b.matchedMembers - a.matchedMembers;
              }
              return (a.name || "").localeCompare(b.name || "");
            });

            setExistingRooms(sortedRooms);
            const totalPages = Math.ceil((data.rooms.totalCount || 0) / limit);
            setHasMore(totalPages > 1);
          }
        } catch (error) {
          console.error("Error reloading:", error);
        } finally {
          setLoadingRooms(false);
        }
      };
      // Always reload (even when no members selected)
      reloadInitial();
      return;
    }

    // Wait for typing to settle before searching (3+ chars)
    if (searchValue.trim().length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        setDebouncedSearch(searchValue.trim());
        setPage(1);
        setExistingRooms([]);
        setHasMore(true);
      }, 400);
    } else {
      // If less than 3 characters, clear results
      setDebouncedSearch("");
      setExistingRooms([]);
      setHasMore(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchValue, selectedMemberIds, company]);

  // Load rooms when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== undefined && debouncedSearch.trim() !== "") {
      loadRooms(1, debouncedSearch.trim());
    }
  }, [debouncedSearch]);

  // Load more when page changes (for pagination)
  useEffect(() => {
    if (page > 1) {
      loadRooms(page, debouncedSearch?.trim() || "", true);
    }
  }, [page, debouncedSearch]);

  // Load existing rooms with pagination and search
  const loadRooms = async (pageNum = 1, searchQuery = "", append = false) => {
    if (append && (loadingMore || loadingRooms)) return;
    if (!append && loadingRooms && existingRooms.length > 0 && pageNum === 1) return;

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingRooms(true);
      }

      // If no members selected, pass only the current user's ID
      const usersToPass = (selectedMemberIds && selectedMemberIds.length > 0) 
        ? selectedMemberIds 
        : [currentUser?.id];

      const requestBody = {
        users: usersToPass,
        page: pageNum,
        limit: limit,
        search: searchQuery || "",
      };
      if (company && company.id) {
        requestBody.company = company.id;
      }
      const { data } = await callAPI(
        `/building/getSuggestedRooms`,
        {},
        "POST",
        requestBody
      );

      if (data.status && data.rooms && data.rooms.data) {
        // Sort by matchedMembers (highest first), then by name
        const sortedRooms = [...data.rooms.data].sort((a, b) => {
          if (b.matchedMembers !== a.matchedMembers) {
            return b.matchedMembers - a.matchedMembers;
          }
          return (a.name || "").localeCompare(b.name || "");
        });

        if (append) {
          setExistingRooms((prev) => [...prev, ...sortedRooms]);
        } else {
          setExistingRooms(sortedRooms);
        }

        // Check if there are more pages based on totalCount
        const totalPages = Math.ceil((data.rooms.totalCount || 0) / limit);
        setHasMore(pageNum < totalPages);
      } else {
        if (!append) {
          setExistingRooms([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading rooms:", error);
      if (!append) {
        $crud.notify({
          type: "error",
          message: strings.error_loading_groups,
        });
        setExistingRooms([]);
      }
      setHasMore(false);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoadingRooms(false);
      }
    }
  };

  // Handle scroll for infinite pagination
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && hasMore && !loadingMore && !loadingRooms && debouncedSearch !== undefined) {
      setPage((prev) => prev + 1);
    }
  }, [hasMore, loadingMore, loadingRooms, debouncedSearch]);


  // Handle complete - show privacy modal first
  const handleComplete = () => {
    if (!selectedRoom && !roomName.trim()) {
      $crud.notify({
        type: "error",
        message: strings.please_select_or_enter_room_name,
      });
      return;
    }
    // Show privacy confirmation modal
    setShowPrivacyModal(true);
  };

  // Handle privacy choice and proceed with creation
  const handlePrivacyChoice = async (isPrivate) => {
    setShowPrivacyModal(false);

    if (selectedRoom) {
      // Use existing room - create chat group within that room, then open it
      await createGroupChatWithRoom(selectedRoom, isPrivate);
    } else if (roomName.trim()) {
      // Store privacy choice and check if company selection is needed
      setPendingPrivacyChoice(isPrivate);

      // Check if we need company selection
      if (companies.length > 1 && !roomParams.companyId) {
        setShowCompanyDialog(true);
      } else {
        // Create new room first, then create chat group within it, then open it
        await createNewRoomAndChat(roomName.trim(), isPrivate);
      }
    }
  };

  // Handle company selection and continue with room creation
  const handleCompanySelected = async (company) => {
    const updatedParams = {
      companyId: company.id,
      companyName: company.name,
    };
    setRoomParams(updatedParams);
    setShowCompanyDialog(false);

    // Continue with room creation using the stored privacy choice
    // Pass company data directly to avoid state update timing issues
    if (roomName.trim()) {
      await createNewRoomAndChatWithCompany(roomName.trim(), pendingPrivacyChoice, updatedParams);
    }
  };

  // Handle company selection for room creation
  const handleReJoinCompany = async (company) => {
    await handleCompanySelected(company);
  };

  // Create group chat with existing room
  const createGroupChatWithRoom = async (room, isPrivate = false) => {
    try {
      // If loadingStage is not set yet, set it now (for cases where this is called directly)
      if (!loadingStage) {
        setLoading(true);
        setLoadingStage("creating");
      }

      // Create new group chat within the room (similar to AddProjectChatUser.js)
      // selectedMemberIds is already an array of user IDs
      const userIds = [...selectedMemberIds];

      const { data } = await callAPI(
        `/group`,
        {},
        "POST",
        {
          building: room.id,
          groupType: "building",
          isOneToOne: false,
          name: chatGroup.name,
          users: [currentUser?.id, ...userIds],
          isPrivet: isPrivate,
        }
      );

      if (data.status) {
        if (teamsPeople.length > 0 && data.chat && data.chat.id) {
          await attachTeamsCopy({ groupId: data.chat.id, people: teamsPeople, accountId: teamsAccountId, strings, isGerm });
        }
        // Switch to opening stage
        setLoadingStage("opening");
        // Small delay to show the "opening" message
        await new Promise(resolve => setTimeout(resolve, 300));
        // Open the newly created chat group - pass isInternal flag and ensure building ID is included
        // The API may not return the building ID in data.chat, so we explicitly add it
        // Include companyId for internal chats to help MyProject find the correct room
        const chatData = {
          ...data.chat,
          building: data.chat?.building || room.id,
          buildingId: data.chat?.buildingId || room.id,
          isInternal: isInternal,
          companyId: company?.id || roomParams.companyId || data.chat?.companyId,
        };
        if (onComplete) onComplete(chatData);
      } else {
        $crud.notify({
          type: "error",
          message: data.message || (strings.error_creating_group),
        });
        setLoading(false);
        setLoadingStage(null);
      }
    } catch (error) {
      console.error("Error creating group chat:", error);
      $crud.notify({
        type: "error",
        message: strings.error_creating_group,
      });
      setLoading(false);
      setLoadingStage(null);
    }
  };

  // Set room params (for company selection)
  const setRoomParam = (name, value) => {
    setRoomParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Create new room and then group chat (with optional company data)
  const createNewRoomAndChatWithCompany = async (name, isPrivate = false, companyData = null) => {
    try {
      setLoading(true);
      setLoadingStage("creating"); // Show loading overlay immediately

      // Use provided company data, company from props (internal chat), or fall back to state
      const finalCompanyId = companyData?.companyId || company?.id || roomParams.companyId;
      const finalCompanyName = companyData?.companyName || company?.name || roomParams.companyName;

      // Check if this is an internal flow (company provided from member selection) - must be boolean
      const isInternal = !!(company && company.id);

      // Create room - use createInternalBuilding for internal flow, otherwise use regular building endpoint
      const projectData = {
        name: name,
        isHidden: true, // Always create hidden rooms from new message flow
      };

      if (finalCompanyName) {
        projectData.companyName = finalCompanyName;
      }
      if (finalCompanyId) {
        projectData.companyId = finalCompanyId;
      }

      // Use internal building API for internal flow, otherwise use regular building API
      const endpoint = isInternal ? "/building/createInternalBuilding" : "/building";
      const { data } = await callAPI(
        endpoint,
        "",
        "POST",
        projectData
      );


      if (data.status) {
        // Add selected members to the newly created room
        const roomId = data.data.id || data.data;
        const finalCompanyId = companyData?.companyId || company?.id || roomParams.companyId;

        if (selectedMemberIds && selectedMemberIds.length > 0) {
          try {
            // Format users array: [{ user: userId1 }, { user: userId2 }, ...]
            const usersToAdd = selectedMemberIds
              .filter(id => id !== currentUser?.id)
              .map(id => ({ user: id }));

            if (usersToAdd.length > 0) {
              const addMembersUrl = finalCompanyId
                ? `/building/${roomId}/add?companyId=${finalCompanyId}`
                : `/building/${roomId}/add`;

              const { data: addData } = await callAPI(
                addMembersUrl,
                {},
                "PUT",
                {
                  users: usersToAdd,
                  // Members picked while creating the room join the room + "All" group
                  // immediately (like internal rooms), instead of pending invitations.
                  autoJoin: true,
                }
              );

              if (!addData.status) {
                console.warn("Warning: Failed to add members to room:", addData.message);
                // Continue anyway - members might already be in the room
              }
            }
          } catch (error) {
            console.error("Error adding members to room:", error);
            // Continue anyway - members might already be in the room
          }
        }

        // Now create group chat with the new room, then open it
        await createGroupChatWithRoom(data.data, isPrivate);
      } else {
        $crud.notify({
          type: "error",
          message: data.message || (strings.error_creating_room),
        });
        setLoading(false);
        setLoadingStage(null);
      }
    } catch (error) {
      console.error("Error creating room:", error);
      $crud.notify({
        type: "error",
        message: strings.error_creating_room,
      });
      setLoading(false);
      setLoadingStage(null);
    }
  };

  // Create new room and then group chat (original function for initial call)
  const createNewRoomAndChat = async (name, isPrivate = false) => {
    // Skip company selection if company is already provided (internal flow)
    if (isInternal && company && company.id) {
      await createNewRoomAndChatWithCompany(name, isPrivate, {
        companyId: company.id,
        companyName: company.name,
      });
      return;
    }
    
    // Check if we need company selection (only for non-internal flows)
    if (companies.length > 1 && !roomParams.companyId) {
      setShowCompanyDialog(true);
      return;
    }

    // Call the function with company data
    await createNewRoomAndChatWithCompany(name, isPrivate);
  };

  return (
    <div
      className={`create-message-dialog room-selection${isInternal ? " room-selection--internal" : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Loading Overlay */}
      {loadingStage && (
        <div className="create-message-loading-overlay">
          <div className="create-message-loading-content">
            <div className="create-message-loading-spinner">
              <Oval
                height={50}
                width={50}
                color={primaryColor}
                secondaryColor={isInternal ? "rgba(var(--kjuup-internal-rgb), 0.3)" : "rgba(var(--kjuup-accent-rgb), 0.3)"}
                wrapperStyle={{}}
                visible={true}
                ariaLabel="loading"
                strokeWidth={4}
              />
            </div>
            <div className="create-message-loading-stages">
              <div className={`create-message-loading-stage ${loadingStage === "creating" ? "active" : loadingStage === "opening" ? "completed" : ""}`}>
                <span className="stage-icon">
                  {loadingStage === "opening" ? (
                    <span className="material-symbols-outlined room-selection__stage-check">check_circle</span>
                  ) : (
                    <span className={`stage-number room-selection__stage-number room-selection__stage-number--border-on${loadingStage === "creating" ? " room-selection__stage-number--text-on" : ""}`}>1</span>
                  )}
                </span>
                <span className={`stage-text ${loadingStage === "creating" ? "active" : loadingStage === "opening" ? "completed" : ""} room-selection__stage-text${loadingStage === "creating" || loadingStage === "opening" ? " room-selection__stage-text--on" : ""}`}>
                  {strings.creating_structure}
                </span>
              </div>
              <div className={`create-message-loading-stage ${loadingStage === "opening" ? "active" : ""}`}>
                <span className="stage-icon">
                  <span className={`stage-number room-selection__stage-number${loadingStage === "opening" ? " room-selection__stage-number--border-on room-selection__stage-number--text-on" : ""}`}>2</span>
                </span>
                <span className={`stage-text ${loadingStage === "opening" ? "active" : ""} room-selection__stage-text${loadingStage === "opening" ? " room-selection__stage-text--on" : ""}`}>
                  {strings.opening_chat}
                </span>
              </div>
            </div>
            <p className="create-message-loading-subtext">{strings.please_wait}</p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="create-message-header">
        <button
          onClick={onBack}
          className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral"
        >
          <span className="material-symbols-outlined">keyboard_arrow_left</span>
        </button>
        <h5 className="create-message-header-title-sm">
          {strings.set_project_room}
        </h5>
        <button
          onClick={handleComplete}
          disabled={loading || (!selectedRoom && !roomName.trim())}
          className={`kj-btn kj-btn--ghost kj-btn--sm${isInternal ? ' kj-btn--internal' : ''}`}
        >
          {loading ? (
            <Oval
              height={20}
              width={20}
              color={primaryColor}
              wrapperStyle={{}}
              visible={true}
              ariaLabel="loading"
              strokeWidth={5}
            />
          ) : (
            <>
              {strings.continue_btn}
              {!loading && <span className="material-symbols-outlined">keyboard_arrow_right</span>}
            </>
          )}
        </button>
      </div>



      {/* Search/Input Bar */}
      <div className="create-message-search-section-no-border">
        <div className="create-message-search-wrapper">
          <input
            ref={inputRef}
            type="text"
            autoFocus
            value={selectedRoom ? selectedRoom.name : (searchValue || roomName)}
            onChange={(e) => {
              const value = e.target.value;
              if (selectedRoom) {
                setSelectedRoom(null);
              }
              setSearchValue(value);
              if (!value) {
                setRoomName("");
              } else if (!selectedRoom) {
                setRoomName(value);
              }
            }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder={strings.name_project_room}
            className={`create-message-search-input-no-icon ${isInternal ? 'internal' : ''} ${inputFocused ? 'focused room-selection__input--focused' : ''}`}
          />
        </div>
      </div>

      {/* Show group chat being created */}
      {chatGroup && chatGroup.isNew && (
        <div className="create-message-chatgroup-badge">
          <span className="create-message-chatgroup-badge-label">
            {strings.subject_label}
          </span>
          <span className="create-message-chatgroup-badge-value">
            {chatGroup.name}
          </span>
        </div>
      )}

      {/* Existing Rooms List */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="create-message-list"
      >
        {loadingRooms && existingRooms.length === 0 ? (
          <div className="create-message-list-loading">
            <Oval
              height={40}
              width={40}
              color={primaryColor}
              wrapperStyle={{}}
              visible={true}
              ariaLabel="loading"
              strokeWidth={5}
            />
          </div>
        ) : existingRooms.length === 0 ? (
          <div className="create-message-list-empty">
            <p>{strings.no_rooms_found}</p>
          </div>
        ) : (
          <>
            <p className="create-message-list-section-title">
              {strings.matching_existing_project_rooms}
            </p>
            {existingRooms.map((room) => (
              <RoomItem
                key={room.id}
                room={room}
                selected={selectedRoom?.id === room.id}
                onSelect={() => {
                  setSelectedRoom(room);
                  setRoomName("");
                }}
                strings={strings}
                KJUUP_COLORS={KJUUP_COLORS}
                primaryColor={primaryColor}
                isInternal={isInternal}
              />
            ))}
            {loadingMore && (
              <div className="create-message-loading-more">
                <Oval
                  height={30}
                  width={30}
                  color={primaryColor}
                  wrapperStyle={{}}
                  visible={true}
                  ariaLabel="loading"
                  strokeWidth={4}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Privacy Confirmation Modal */}
      {showPrivacyModal && (
        <div
          className="create-message-modal-overlay"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="create-message-modal"
          >
            <h4 className="create-message-modal-title">
              {strings.professional_or_private}
            </h4>
            <p className="create-message-modal-text">
              {strings.privacy_modal_text_updated}
            </p>
            <div className="create-message-modal-buttons">
              <button
                onClick={() => handlePrivacyChoice(true)}
                disabled={loading}
                className="kj-btn kj-btn--secondary create-message-modal-btn"
              >
                {strings.private}
              </button>
              <button
                onClick={() => handlePrivacyChoice(false)}
                disabled={loading}
                className={`kj-btn kj-btn--primary${isInternal ? ' kj-btn--internal' : ''} create-message-modal-btn`}
              >
                {strings.professional}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Selection Dialog */}
      {showCompanyDialog && (
        <CompanyListDialog
          handleClose={() => setShowCompanyDialog(false)}
          setParam={setRoomParam}
          companies={companies}
          handleReJoinCompany={handleReJoinCompany}
        />
      )}
    </div>
  );
}

// Room Item Component
const RoomItem = ({ room, selected, onSelect, strings, KJUUP_COLORS, primaryColor, isInternal }) => {
  const currentUser = $user.get();

  // Get member names from room.users array (provided by API)
  // Show all users as "firstName lastName", separated by comma
  // Exclude current user - if only current user, show "no members"
  const getMemberNames = () => {
    if (!room.users || room.users.length === 0) {
      return "";
    }

    const names = room.users
      .filter(m => m.id !== currentUser?.id) // Exclude current user
      .map(m => {
        const firstName = (m.firstName || "").trim();
        const lastName = (m.lastName || "").trim();
        return `${firstName} ${lastName}`.trim();
      })
      .filter(Boolean);

    // If no other members besides current user, return empty string (will show "no members")
    if (names.length === 0) {
      return "";
    }

    return names.join(", ");
  };

  const memberNames = getMemberNames();

  return (
    <div
      onClick={onSelect}
      className={`create-message-room-item ${selected ? 'selected room-selection__item--selected' : ''} ${isInternal ? 'internal' : ''}`}
    >
      <div className="create-message-room-content">
        <p className="create-message-room-title">
          {room.name || strings.unnamed_room}
        </p>
        <p className="create-message-room-subtitle">
          {memberNames || strings.no_members}
        </p>
      </div>
      <div className="create-message-room-checkbox">
        <div className={`custom_checkbox room-selection__checkbox ${isInternal ? 'internal' : ''}`}>
          <label className="role-label room-selection__checkbox-label">
            <input
              checked={selected}
              disabled
              type="checkbox"
              value={room.id}
            />
            <span className={`checkmark-label room-selection__checkmark ${isInternal ? 'internal' : ''}`} />
          </label>
        </div>
      </div>
    </div>
  );
};

export default RoomSelection;

